import { useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Tooltip,
} from 'antd';
import {
  CheckOutlined,
  CopyOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import '../CapacityButtons.less';
import '../CapacityDrawer.less';
import {
  useCreateNewClusterSize,
  useGetClusterSizeRoles,
  useUpdateClusterSize,
} from 'hooks/capacityManagementQueries';
import {
  ClusterSizesPerClusterCodeItem,
  NodeLabelObj,
  OperatorPerPlatformItem,
} from 'types/capacityManagement.types';
import { getSizeExamples, validateSizeName } from 'utils/sizeValidation';
import { useAccess } from 'hooks/useAccess';
import { formatMemory, SizeResources } from '../capacitySummary';
import AffectedClusters from '../AffectedClusters/AffectedClusters';
import DeleteClusterSizeAction from '../DeleteClusterSizeAction/DeleteClusterSizeAction';
import NodeLabelsRenderer from '../NodeLabelsRenderer/NodeLabelsRenderer';
import IstioFields from './IstioFields';

export type SizePrefill = Partial<ClusterSizesPerClusterCodeItem> & {
  rolesIDs?: number[];
  clickedOperatorID?: number;
  clickedOperatorIDs?: number[];
};

export type SizeDrawerTarget =
  | {
      kind: 'edit';
      cell: ClusterSizesPerClusterCodeItem;
      operator: OperatorPerPlatformItem;
      baseline?: SizeResources;
    }
  | { kind: 'create'; prefill?: SizePrefill };

type OverCommitment = {
  excluded: boolean;
  analyticsValue?: number | false;
  loaderValue?: number | false;
};

type FormValues = {
  name?: string;
  displayName?: string;
  rolesIDs?: number[];
  dsize?: number | null;
  memoryRequest?: number | null;
  memoryLimit?: number | null;
  cpu?: number | null;
  requireHyperDisk?: boolean;
  overCommitmentEnabled?: boolean;
  analyticsValue?: number | null;
  loaderValue?: number | null;
  istioConfigEnabled?: boolean;
  istioProxyConcurrency?: number | null;
  istioProxyCpuRequest?: string | null;
  istioProxyCpuLimit?: string | null;
  istioProxyMemoryRequest?: string | null;
  istioProxyMemoryLimit?: string | null;
};

const EMPTY_LABELS: NodeLabelObj = { analytics: '', loader: '' };

const ISTIO_FIELDS = [
  'istioProxyConcurrency',
  'istioProxyCpuRequest',
  'istioProxyCpuLimit',
  'istioProxyMemoryRequest',
  'istioProxyMemoryLimit',
] as const;

function parseOverCommitment(value: unknown): OverCommitment {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return { excluded: true };
    }
  }
  return (value as OverCommitment) ?? { excluded: true };
}

function initialValuesFor(target: SizeDrawerTarget): FormValues {
  const source: SizePrefill | undefined =
    target.kind === 'edit' ? target.cell : target.prefill;
  const overCommitment = parseOverCommitment(source?.overCommitment);
  return {
    name: source?.name,
    displayName: source?.displayName,
    rolesIDs:
      source?.rolesIDs ?? source?.roles?.map(role => role.roleID) ?? undefined,
    dsize: source?.dsize ? Number(source.dsize) : null,
    memoryRequest: source?.memoryRequest ?? null,
    memoryLimit: source?.memoryLimit ?? null,
    cpu: source?.cpu ?? null,
    requireHyperDisk: !!source?.requireHyperDisk,
    overCommitmentEnabled: !overCommitment.excluded,
    analyticsValue: overCommitment.analyticsValue || null,
    loaderValue: overCommitment.loaderValue || null,
    istioConfigEnabled: !!source?.istioConfigEnabled,
    istioProxyConcurrency: source?.istioProxyConcurrency ?? null,
    istioProxyCpuRequest: source?.istioProxyCpuRequest ?? null,
    istioProxyCpuLimit: source?.istioProxyCpuLimit ?? null,
    istioProxyMemoryRequest: source?.istioProxyMemoryRequest ?? null,
    istioProxyMemoryLimit: source?.istioProxyMemoryLimit ?? null,
  };
}

// Istio values are only sent while the custom configuration is on; null clears them.
function istioPayload(values: FormValues) {
  const enabled = !!values.istioConfigEnabled;
  return {
    istioConfigEnabled: enabled,
    ...Object.fromEntries(
      ISTIO_FIELDS.map(field => [
        field,
        enabled ? values[field] || null : null,
      ]),
    ),
  };
}

function hasRequiredLabels(labels?: NodeLabelObj) {
  return !!labels?.analytics?.trim() && !!labels?.loader?.trim();
}

function formatLabels(labels?: NodeLabelObj) {
  return Object.entries(labels ?? {})
    .map(([key, value]) => `${key}: ${value}`)
    .join(', ');
}

function SizeDrawer({
  target,
  operators,
  cloudProviderID,
  canEdit,
  onClose,
}: Readonly<{
  target: SizeDrawerTarget;
  operators: OperatorPerPlatformItem[];
  cloudProviderID: number;
  canEdit: boolean;
  onClose: () => void;
}>) {
  const { can } = useAccess();
  const isCreate = target.kind === 'create';
  const [form] = Form.useForm<FormValues>();
  const initialValues = useMemo(() => initialValuesFor(target), [target]);
  const [values, setValues] = useState<FormValues>(initialValues);

  const initialOperatorIDs = useMemo(() => {
    if (target.kind === 'edit') return [target.operator.id];
    const { clickedOperatorIDs, clickedOperatorID } = target.prefill ?? {};
    if (clickedOperatorIDs?.length) return clickedOperatorIDs;
    return clickedOperatorID ? [clickedOperatorID] : [];
  }, [target]);
  const [selectedOperatorIDs, setSelectedOperatorIDs] =
    useState<number[]>(initialOperatorIDs);

  const startingLabels =
    (target.kind === 'edit'
      ? target.cell.nodeLabelObj
      : target.prefill?.nodeLabelObj) ?? EMPTY_LABELS;
  const [labelsByOperator, setLabelsByOperator] = useState<
    Record<number, NodeLabelObj>
  >(() =>
    Object.fromEntries(initialOperatorIDs.map(id => [id, startingLabels])),
  );
  const [labelsError, setLabelsError] = useState<string>();

  const { data: roles, isLoading: isRolesLoading } = useGetClusterSizeRoles({
    isForCustomSize:
      isCreate || !!(target.kind === 'edit' && target.cell.isCustomSize),
  });
  const { mutateAsync: createSize, isPending: isCreating } =
    useCreateNewClusterSize();
  const { mutateAsync: updateSize, isPending: isUpdating } =
    useUpdateClusterSize();

  const selectedOperators = operators.filter(operator =>
    selectedOperatorIDs.includes(operator.id),
  );
  const sizeName = values.name || 'this size';
  const operatorWord = (count: number) =>
    `${count} operator${count === 1 ? '' : 's'}`;

  function setLabels(operatorID: number, labels: NodeLabelObj) {
    setLabelsError(undefined);
    setLabelsByOperator(prev => ({ ...prev, [operatorID]: labels }));
  }

  function toggleOperator(operatorID: number, checked: boolean) {
    setLabelsError(undefined);
    setSelectedOperatorIDs(prev =>
      checked ? [...prev, operatorID] : prev.filter(id => id !== operatorID),
    );
    if (checked && !labelsByOperator[operatorID]) {
      // A newly ticked operator starts from the labels already filled in for another one.
      const firstFilled = selectedOperatorIDs
        .map(id => labelsByOperator[id])
        .find(hasRequiredLabels);
      setLabels(operatorID, firstFilled ?? startingLabels);
    }
  }

  const changes = useMemo(() => {
    if (target.kind !== 'edit') return [];
    const roleName = (ids?: number[]) =>
      (ids ?? [])
        .map(id => roles?.find(role => role.id === id)?.displayName ?? id)
        .join(', ') || 'none';
    const overCommitmentText = (v: FormValues) =>
      v.overCommitmentEnabled
        ? `on (analytics ${v.analyticsValue ?? '—'}, loader ${v.loaderValue ?? '—'})`
        : 'off';
    const istioText = (v: FormValues) =>
      v.istioConfigEnabled
        ? ISTIO_FIELDS.map(field => v[field] ?? '—').join(' · ')
        : 'default';
    const rows = [
      {
        label: 'Memory req / limit',
        before: `${initialValues.memoryRequest} / ${initialValues.memoryLimit} GB`,
        after: `${values.memoryRequest ?? '—'} / ${values.memoryLimit ?? '—'} GB`,
      },
      {
        label: 'CPU',
        before: `${initialValues.cpu} vCPU`,
        after: `${values.cpu ?? '—'} vCPU`,
      },
      {
        label: 'HyperDisk',
        before: initialValues.requireHyperDisk ? 'required' : 'not required',
        after: values.requireHyperDisk ? 'required' : 'not required',
      },
      {
        label: 'Roles',
        before: roleName(initialValues.rolesIDs),
        after: roleName(values.rolesIDs),
      },
      {
        label: 'Disk size',
        before: initialValues.dsize ? `${initialValues.dsize} GB` : 'default',
        after: values.dsize ? `${values.dsize} GB` : 'default',
      },
      {
        label: 'Node labels',
        before: formatLabels(target.cell.nodeLabelObj),
        after: formatLabels(labelsByOperator[target.operator.id]),
      },
      {
        label: 'Over-commitment',
        before: overCommitmentText(initialValues),
        after: overCommitmentText(values),
      },
      {
        label: 'Istio proxy',
        before: istioText(initialValues),
        after: istioText(values),
      },
    ];
    return rows.filter(row => row.before !== row.after);
  }, [initialValues, labelsByOperator, roles, target, values]);

  async function handleSave() {
    let formValues: FormValues;
    try {
      formValues = await form.validateFields();
    } catch {
      return;
    }
    const allValues = { ...values, ...formValues };
    if (selectedOperatorIDs.length === 0) {
      setLabelsError('Select at least one operator.');
      return;
    }
    const missingLabels = selectedOperators.filter(
      operator => !hasRequiredLabels(labelsByOperator[operator.id]),
    );
    if (missingLabels.length > 0) {
      setLabelsError(
        `Fill in the analytics and loader node labels for ${missingLabels
          .map(operator => operator.clusterCode)
          .join(', ')}.`,
      );
      return;
    }

    const resources = {
      memoryRequest: allValues.memoryRequest!,
      memoryLimit: allValues.memoryLimit!,
      memoryText: allValues.memoryLimit!,
      cpu: allValues.cpu!,
      requireHyperDisk: !!allValues.requireHyperDisk,
    };

    try {
      if (target.kind === 'create') {
        const { status } = await createSize({
          // The create endpoint takes one entry per operator, each with its own node labels.
          newClusterSize: {
            name: allValues.name,
            displayName: allValues.displayName,
            cloudProviderID,
            rolesIDs: allValues.rolesIDs,
            operatorClusterCodes: selectedOperators.map(operator => ({
              code: operator.clusterCode,
              label: labelsByOperator[operator.id],
              ...resources,
              dsize: allValues.dsize,
            })),
            ...istioPayload(allValues),
          } as any,
        });
        if (String(status).startsWith('2')) onClose();
      } else {
        const { status } = await updateSize({
          sizeID: target.cell.id,
          updatedClusterSize: {
            ...resources,
            dsize: allValues.dsize
              ? String(allValues.dsize)
              : target.cell.dsize,
            rolesIDs: allValues.rolesIDs,
            nodeLabel: labelsByOperator[target.operator.id],
            overCommitment: {
              excluded: !allValues.overCommitmentEnabled,
              analyticsValue: allValues.overCommitmentEnabled
                ? allValues.analyticsValue || false
                : false,
              loaderValue: allValues.overCommitmentEnabled
                ? allValues.loaderValue || false
                : false,
            },
            ...istioPayload(allValues),
          },
        });
        if (String(status).startsWith('2')) onClose();
      }
    } catch {
      // The mutation hooks already show the backend's error message.
    }
  }

  const saveDisabled =
    !canEdit ||
    (isCreate ? selectedOperatorIDs.length === 0 : changes.length === 0);
  const baseline = target.kind === 'edit' ? target.baseline : undefined;
  const differsFromBaseline =
    !!baseline &&
    (values.memoryRequest !== baseline.memoryRequest ||
      values.memoryLimit !== baseline.memoryLimit ||
      values.cpu !== baseline.cpu);

  return (
    <Drawer
      open
      width={680}
      onClose={onClose}
      className="capacity-drawer"
      title={
        <div className="capacity-drawer__title">
          <span className="capacity-drawer__eyebrow">
            {isCreate ? 'Create size' : 'Edit size'}
          </span>
          {target.kind === 'edit' ? (
            <span>
              <span className="capacity-drawer__mono">{target.cell.name}</span>{' '}
              on{' '}
              <span className="capacity-drawer__mono">
                {target.operator.clusterCode}
              </span>
            </span>
          ) : (
            <span>
              {values.name ? (
                <span className="capacity-drawer__mono">{values.name}</span>
              ) : (
                'New size'
              )}{' '}
              on {operatorWord(selectedOperatorIDs.length)}
            </span>
          )}
        </div>
      }
      footer={
        <div className="capacity-drawer__footer">
          {target.kind === 'edit' && (
            <span className="capacity-drawer__footer-start">
              <DeleteClusterSizeAction
                clusterSizeID={target.cell.id}
                clusterSizeDisplayName={target.cell.displayName}
                operatorAlias={target.operator.clusterCode}
                isLoading={isUpdating || !can('sizes.delete')}
                canBeDeleted={target.cell.canBeDeleted}
                className="capacity-btn capacity-btn--lg capacity-btn--danger-text"
                close={onClose}
              />
            </span>
          )}
          <Button
            className="capacity-btn capacity-btn--lg capacity-btn--outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Tooltip
            title={canEdit ? undefined : 'You don’t have sufficient permission'}
          >
            <Button
              className="capacity-btn capacity-btn--lg capacity-btn--primary"
              icon={
                isCreate ? (
                  <PlusOutlined aria-hidden="true" />
                ) : (
                  <CheckOutlined aria-hidden="true" />
                )
              }
              loading={isCreating || isUpdating}
              disabled={saveDisabled}
              onClick={handleSave}
            >
              {isCreate
                ? `Create on ${operatorWord(selectedOperatorIDs.length)}`
                : 'Save changes'}
            </Button>
          </Tooltip>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onValuesChange={(_, all) => setValues(prev => ({ ...prev, ...all }))}
        className="capacity-drawer__body"
        requiredMark="optional"
      >
        <section className="capacity-drawer__section">
          <h3 className="capacity-drawer__heading">Size</h3>
          <div className="capacity-drawer__grid capacity-drawer__grid--2">
            <Form.Item
              name="name"
              label={
                <span className="capacity-drawer__label">
                  Name
                  <Tooltip
                    title={`A main size, optionally with a prefix or suffix. Examples: ${getSizeExamples().join(', ')}`}
                  >
                    <InfoCircleOutlined aria-label="Naming rules" />
                  </Tooltip>
                </span>
              }
              rules={[
                { required: true, message: 'Enter a name' },
                {
                  validator(_, value) {
                    if (!isCreate || !value) return Promise.resolve();
                    const result = validateSizeName(value);
                    return result.isValid
                      ? Promise.resolve()
                      : Promise.reject(new Error(result.message));
                  },
                },
              ]}
            >
              <Input disabled={!isCreate} placeholder="e.g. pipelines-large" />
            </Form.Item>
            <Form.Item
              name="displayName"
              label="Display name"
              rules={[{ required: true, message: 'Enter a display name' }]}
            >
              <Input disabled={!isCreate} />
            </Form.Item>
          </div>
          <Form.Item
            name="rolesIDs"
            label="Roles"
            rules={[{ required: true, message: 'Select at least one role' }]}
          >
            <Select
              mode="multiple"
              placeholder="Select at least one role"
              loading={isRolesLoading}
              optionFilterProp="label"
              options={roles?.map(role => ({
                value: role.id,
                label: role.displayName,
              }))}
            />
          </Form.Item>
        </section>

        <section className="capacity-drawer__section">
          <h3 className="capacity-drawer__heading">
            {isCreate ? 'Operators and node labels' : 'Node labels'}
          </h3>
          {isCreate && (
            <div className="capacity-drawer__heading-row">
              <span className="capacity-drawer__muted">
                {selectedOperatorIDs.length} of {operators.length} selected
              </span>
              <span>
                <Button
                  className="capacity-btn capacity-btn--sm capacity-btn--link"
                  onClick={() =>
                    operators
                      .filter(
                        operator => !selectedOperatorIDs.includes(operator.id),
                      )
                      .forEach(operator => toggleOperator(operator.id, true))
                  }
                >
                  Select all
                </Button>
                <Button
                  className="capacity-btn capacity-btn--sm capacity-btn--link"
                  onClick={() => setSelectedOperatorIDs([])}
                >
                  Clear
                </Button>
              </span>
            </div>
          )}
          <ul className="capacity-drawer__operators">
            {(isCreate ? operators : [target.operator]).map(operator => {
              const selected = selectedOperatorIDs.includes(operator.id);
              return (
                <li key={operator.id} className="capacity-drawer__operator">
                  <div className="capacity-drawer__operator-head">
                    {isCreate ? (
                      <Checkbox
                        checked={selected}
                        onChange={event =>
                          toggleOperator(operator.id, event.target.checked)
                        }
                      >
                        <span className="capacity-drawer__mono">
                          {operator.clusterCode}
                        </span>
                      </Checkbox>
                    ) : (
                      <span className="capacity-drawer__mono">
                        {operator.clusterCode}
                      </span>
                    )}
                    <span className="capacity-drawer__muted">
                      {operator.region}
                    </span>
                  </div>
                  {selected && (
                    <div className="capacity-drawer__operator-labels">
                      <NodeLabelsRenderer
                        isCreateNewSize={isCreate}
                        currentNodeLabelRow={{
                          label: labelsByOperator[operator.id] ?? EMPTY_LABELS,
                        }}
                        onNodeLabelsChangeForm={({ selectedNodeLabels }) =>
                          setLabels(operator.id, selectedNodeLabels)
                        }
                      />
                      {isCreate && selectedOperatorIDs.length > 1 && (
                        <Button
                          className="capacity-btn capacity-btn--sm capacity-btn--link"
                          icon={<CopyOutlined aria-hidden="true" />}
                          disabled={
                            !hasRequiredLabels(labelsByOperator[operator.id])
                          }
                          onClick={() =>
                            selectedOperatorIDs.forEach(id =>
                              setLabels(id, labelsByOperator[operator.id]),
                            )
                          }
                        >
                          Use these labels for all selected operators
                        </Button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          {labelsError && (
            <p className="capacity-drawer__error" role="alert">
              {labelsError}
            </p>
          )}
        </section>

        <section className="capacity-drawer__section">
          <div className="capacity-drawer__heading-row">
            <h3 className="capacity-drawer__heading">Resources</h3>
            {baseline && differsFromBaseline && (
              <Button
                className="capacity-btn capacity-btn--sm capacity-btn--subtle"
                icon={<UndoOutlined aria-hidden="true" />}
                onClick={() => {
                  form.setFieldsValue(baseline);
                  setValues(prev => ({ ...prev, ...baseline }));
                }}
              >
                Fill with baseline · {formatMemory(baseline)} · {baseline.cpu}{' '}
                vCPU
              </Button>
            )}
          </div>
          {isCreate && selectedOperatorIDs.length > 1 && (
            <p className="capacity-drawer__hint">
              Applies to every selected operator. Fine-tune one later with Edit.
            </p>
          )}
          <div className="capacity-drawer__grid capacity-drawer__grid--3">
            <Form.Item
              name="memoryRequest"
              label="Memory request (GB)"
              dependencies={['memoryLimit']}
              rules={[
                { required: true, message: 'Required' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const limit = getFieldValue('memoryLimit');
                    return value && limit && value > limit
                      ? Promise.reject(new Error('Must not exceed the limit'))
                      : Promise.resolve();
                  },
                }),
              ]}
            >
              <InputNumber min={1} max={10000} />
            </Form.Item>
            <Form.Item
              name="memoryLimit"
              label="Memory limit (GB)"
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber min={1} max={10000} />
            </Form.Item>
            <Form.Item
              name="cpu"
              label="CPU (vCPU)"
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber min={1} max={1000} />
            </Form.Item>
          </div>
          <Form.Item name="requireHyperDisk" valuePropName="checked" noStyle>
            <Checkbox>
              Requires HyperDisk{' '}
              <span className="capacity-drawer__muted">
                (for node pools like C4D and M4D)
              </span>
            </Checkbox>
          </Form.Item>
          {target.kind === 'edit' && (
            <p className="capacity-drawer__hint">
              Current IPU {Number(Number(target.cell.ipu).toFixed(2))}. The IPU
              is recalculated from CPU and memory when you save.
            </p>
          )}
        </section>

        {target.kind === 'edit' && (
          <section className="capacity-drawer__section">
            <div className="capacity-drawer__heading-row">
              <h3 className="capacity-drawer__heading">Over-commitment</h3>
              <Form.Item
                name="overCommitmentEnabled"
                valuePropName="checked"
                noStyle
              >
                <Switch aria-label="Over-commitment" />
              </Form.Item>
            </div>
            {values.overCommitmentEnabled && (
              <div className="capacity-drawer__grid capacity-drawer__grid--2">
                <Form.Item
                  name="analyticsValue"
                  label="Analytics value"
                  rules={[
                    {
                      required: true,
                      message: 'Required while over-commitment is on',
                    },
                  ]}
                >
                  <InputNumber min={1} />
                </Form.Item>
                <Form.Item
                  name="loaderValue"
                  label="Loader value"
                  rules={[
                    {
                      required: true,
                      message: 'Required while over-commitment is on',
                    },
                  ]}
                >
                  <InputNumber min={1} />
                </Form.Item>
              </div>
            )}
          </section>
        )}

        <section className="capacity-drawer__section">
          <h3 className="capacity-drawer__heading">Disk</h3>
          <Form.Item
            name="dsize"
            label="Disk size (GB)"
            extra="Only affects clusters created after this change."
          >
            <InputNumber
              min={1}
              max={1000}
              placeholder="Default"
              addonAfter="GB"
            />
          </Form.Item>
        </section>

        <IstioFields
          sizeCpu={values.cpu}
          sizeMemoryLimit={values.memoryLimit}
        />

        <section className="capacity-drawer__section">
          <h3 className="capacity-drawer__heading">
            {isCreate ? 'What will be created' : 'What will change'}
          </h3>
          {isCreate ? (
            <p className="capacity-drawer__muted">
              {selectedOperatorIDs.length === 0
                ? 'Select at least one operator.'
                : `${sizeName} on ${selectedOperators
                    .map(operator => operator.clusterCode)
                    .join(', ')}, with ${values.memoryRequest ?? '—'} / ${
                    values.memoryLimit ?? '—'
                  } GB and ${values.cpu ?? '—'} vCPU.`}
            </p>
          ) : changes.length === 0 ? (
            <p className="capacity-drawer__muted">No changes yet.</p>
          ) : (
            <table className="capacity-drawer__preview">
              <tbody>
                {changes.map(change => (
                  <tr key={change.label}>
                    <th scope="row">{change.label}</th>
                    <td>
                      <s className="capacity-drawer__muted">{change.before}</s>{' '}
                      → <b>{change.after}</b>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {target.kind === 'edit' && (
          <AffectedClusters
            sizeIDs={[target.cell.id]}
            sizeName={target.cell.name}
          />
        )}
      </Form>
    </Drawer>
  );
}

export default SizeDrawer;
